import { useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";
import { Container, Box, Typography, Grid } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import './contatti.scss';

gsap.registerPlugin(ScrollTrigger);

function Contatti() {
  const heroRef = useRef<HTMLDivElement>(null);
  const recapitiTitleRef = useRef<HTMLHeadingElement>(null);
  const ufficioTitleRef = useRef<HTMLHeadingElement>(null);

  // Split Text Animation
  const animateSplitText = (element: HTMLElement) => {
    const text = element.textContent || '';
    element.innerHTML = text
      .split('')
      .map(char => char === ' ' ? ' ' : `<span class="split-char">${char}</span>`)
      .join('');

    const chars = element.querySelectorAll('.split-char');
    gsap.fromTo(chars,
      { y: 50, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.6,
        stagger: 0.03,
        ease: "power3.out",
        scrollTrigger: {
          trigger: element,
          start: "top 80%",
          toggleActions: "play none none none"
        }
      }
    );
  };

  useEffect(() => {
    // Delay animations to let PageTransition complete
    const timer = setTimeout(() => {
      if (heroRef.current) {
        gsap.fromTo(heroRef.current,
          { opacity: 0, y: 30 },
          { opacity: 1, y: 0, duration: 1, ease: "power2.out" }
        );
      }

      // Animate split text titles
      if (recapitiTitleRef.current) {
        animateSplitText(recapitiTitleRef.current);
      }
      if (ufficioTitleRef.current) {
        animateSplitText(ufficioTitleRef.current);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Contatti | Fabbri Agricoltura - Richiedi Preventivo</title>
        <meta name="description" content="Contatta Fabbri Agricoltura per un preventivo gratuito sui nostri servizi di contoterzismo agricolo. Lavorazioni terreno, semina, raccolta." />
        <meta name="keywords" content="contatti contoterzista, preventivo agricoltura, servizi agricoli preventivo" />
      </Helmet>

      <Box className="contatti-page-agricola" id="top">
        {/* Hero Section */}
        <Box className="contatti-hero-agricola" ref={heroRef}>
          <video
            className="hero-video-agricola"
            autoPlay
            loop
            muted
            playsInline
          >
            <source src="/video/video3.mp4" type="video/mp4" />
          </video>
          <Box className="hero-overlay-agricola"></Box>
          <Container maxWidth="lg" className="hero-content-agricola">
            <Box className="hero-box-agricola">
              <Typography variant="h1" className="hero-title-agricola">
                Contatti
              </Typography>
              <Typography className="hero-description-agricola">
                Hai bisogno di servizi agricoli? Contattami direttamente
              </Typography>
            </Box>
          </Container>
        </Box>

        {/* Contact Section */}
        <Box className="contatti-section">
          <Container maxWidth="lg">
            <Typography variant="h4" className="contatti-section-title split-text" ref={recapitiTitleRef}>
              Recapiti
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box className="info-card-agricola">
                  <Box className="info-icon-box">
                    <PhoneIcon />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" className="info-label">Telefono</Typography>
                    <Typography className="info-value">
                      <a href="tel:00393200441982">+39 3200441982</a>
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box className="info-card-agricola">
                  <Box className="info-icon-box">
                    <EmailIcon />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" className="info-label">Email</Typography>
                    <Typography className="info-value">
                      <a href="mailto:fabbri.agricoltura@outlook.it">fabbri.agricoltura@outlook.it</a>
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box className="info-card-agricola">
                  <Box className="info-icon-box">
                    <LocationOnIcon />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" className="info-label">Sede</Typography>
                    <Typography className="info-value">
                      <a href="https://maps.app.goo.gl/fPrPAXVKa6AsXLmr9" target="_blank" rel="noopener noreferrer">
                        Via Per S. Felice, 3<br />
                        41031 Camposanto (MO)
                      </a>
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box className="info-card-agricola">
                  <Box className="info-icon-box">
                    <AccessTimeIcon />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" className="info-label">Orari</Typography>
                    <Typography className="info-value">
                      Lun - Ven: 8:00 - 18:00<br />
                      Sab: 8:00 - 15:00
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Map Section */}
        <Box className="map-section-agricola">
          <Container maxWidth="lg">
            <Typography variant="h4" className="contatti-section-title split-text" ref={ufficioTitleRef} sx={{ textAlign: 'center', mb: 4 }}>
              Ufficio
            </Typography>
            <Box className="map-container">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d5663.076643684156!2d11.132796712975077!3d44.79021677095033!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x477fbfb38303093b%3A0xbee9b84991fff481!2sFabbri%20Agricoltura!5e0!3m2!1sit!2sit!4v1770542362393!5m2!1sit!2sit"
                width="100%"
                height="450"
                style={{ border: 0, borderRadius: '16px' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Mappa Fabbri Agricoltura"
              />
            </Box>
          </Container>
        </Box>
      </Box>
    </>
  );
}

export default Contatti;
